/**
 * Client wrapper for the Feedback feature (bug reports / feature requests).
 *
 * Stored in Firestore at `feedback/{docId}`. Any authenticated dashboard user
 * may submit; reads and status updates are restricted to admins by
 * `firestore.rules` (`callerRole() == 'admin'`) — real DB-level enforcement,
 * not just a UI gate.
 */
import {
  addDoc,
  collection,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  type FieldValue,
} from "firebase/firestore";
import { firestore } from "./firebase_config";
import { Constants } from "../constant";
import {
  getServiceDomainLabel,
  ServiceDomain,
  type UserProfile,
} from "./roles";
import type { User } from "firebase/auth";

/** What the reporter is telling us. */
export type FeedbackType = "bug" | "feature";

/**
 * Which part of the product the report is about. Any {@link ServiceDomain},
 * or `general` when it doesn't map to a single service (common for feature
 * requests and cross-cutting bugs).
 */
export type FeedbackService = ServiceDomain | "general";

/** Admin-managed triage state. */
export type FeedbackStatus = "open" | "in_progress" | "resolved" | "wont_fix";

export const FEEDBACK_STATUSES: readonly FeedbackStatus[] = [
  "open",
  "in_progress",
  "resolved",
  "wont_fix",
] as const;

export interface FeedbackReporter {
  uid: string;
  email: string;
  displayName: string | null;
}

/** Firestore document shape stored under `feedback/{docId}` (no `id`). */
export interface FeedbackDoc {
  type: FeedbackType;
  service: FeedbackService;
  title: string;
  description: string;
  status: FeedbackStatus;
  reporter: FeedbackReporter;
  /** Firestore server timestamp; a sentinel while the write is in flight. */
  createdAt: Timestamp | FieldValue;
  /** Schema version for future migrations. */
  v: number;
}

/** A feedback row hydrated for the admin list. */
export interface FeedbackListItem {
  id: string;
  type: FeedbackType;
  service: FeedbackService;
  title: string;
  description: string;
  status: FeedbackStatus;
  reporter: FeedbackReporter;
  /** Unix ms, or null if the server timestamp hasn't materialized yet. */
  createdAtMs: number | null;
}

/** Payload for {@link submitFeedback}. */
export type FeedbackSubmitInput = {
  type: FeedbackType;
  service: FeedbackService;
  title: string;
  description: string;
};

export function getFeedbackServiceLabel(service: FeedbackService): string {
  return service === "general"
    ? "General / other"
    : getServiceDomainLabel(service);
}

const STATUS_LABELS: Record<FeedbackStatus, string> = {
  open: "Open",
  in_progress: "In progress",
  resolved: "Resolved",
  wont_fix: "Won't fix",
};

export function getFeedbackStatusLabel(status: FeedbackStatus): string {
  return STATUS_LABELS[status] ?? status;
}

/** Ordered options (`general` first) for the report form's service picker. */
export const FEEDBACK_SERVICE_OPTIONS: readonly FeedbackService[] = [
  "general",
  ...Object.values(ServiceDomain),
];

export function reporterFromAuth(
  user: User | null,
  profile: UserProfile | null,
): FeedbackReporter | null {
  if (!user?.uid || !user.email) return null;
  return {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName ?? profile?.displayName ?? null,
  };
}

const MAX_TITLE_LEN = 140;
const MAX_DESCRIPTION_LEN = 4000;

/**
 * Append one feedback report. Any authenticated dashboard user may submit;
 * `reporter.uid` must match the caller (enforced by firestore.rules).
 */
export async function submitFeedback(
  input: FeedbackSubmitInput,
  reporter: FeedbackReporter,
): Promise<void> {
  const title = input.title.trim();
  const description = input.description.trim();

  if (!title) throw new Error("A short title is required.");
  if (!description) throw new Error("A description is required.");

  const payload: FeedbackDoc = {
    type: input.type,
    service: input.service,
    title: title.slice(0, MAX_TITLE_LEN),
    description: description.slice(0, MAX_DESCRIPTION_LEN),
    status: "open",
    reporter,
    createdAt: serverTimestamp(),
    v: 1,
  };

  await addDoc(collection(firestore, Constants.FEEDBACK_COLLECTION), payload);
}

/** Update the triage status of a report (admin-only per firestore.rules). */
export async function updateFeedbackStatus(
  id: string,
  status: FeedbackStatus,
): Promise<void> {
  await updateDoc(doc(firestore, Constants.FEEDBACK_COLLECTION, id), {
    status,
  });
}

/** Max reports pulled for the admin list in one read. */
export const FEEDBACK_FETCH_LIMIT = 300;

/**
 * Fetch the most recent reports (newest first) in a single read. Admin-only —
 * `firestore.rules` rejects the list for non-admins.
 */
export async function fetchFeedback(
  max = FEEDBACK_FETCH_LIMIT,
): Promise<FeedbackListItem[]> {
  const col = collection(firestore, Constants.FEEDBACK_COLLECTION);
  const snap = await getDocs(
    query(col, orderBy("createdAt", "desc"), limit(max)),
  );

  return snap.docs.map((d) => {
    const data = d.data() as FeedbackDoc;
    const createdAt = data.createdAt;
    return {
      id: d.id,
      type: data.type,
      service: data.service,
      title: data.title,
      description: data.description,
      status: data.status ?? "open",
      reporter: data.reporter,
      createdAtMs: createdAt instanceof Timestamp ? createdAt.toMillis() : null,
    };
  });
}
